import { useScrollReveal } from '../hooks/useScrollReveal';
import { useParallax } from '../hooks/useParallax';

const sixDimensions = [
  { label: '政治方向', desc: '方向准不准', color: 'primary' },
  { label: '理论阐释', desc: '理论深不深', color: 'accent' },
  { label: '问题意识', desc: '问题强不强', color: 'primary' },
  { label: '案例使用', desc: '案例贴不贴', color: 'accent' },
  { label: '课堂互动', desc: '互动有没有发生', color: 'primary' },
  { label: '价值引导', desc: '价值是否自然进入', color: 'accent' },
];

export default function P9Complexity({ forceVisible = false }: { forceVisible?: boolean }) {
  const { ref: titleRef, isVisible: titleVisible } = useScrollReveal(0.12, forceVisible);
  const { ref: orb1Ref, offset: orb1Y } = useParallax(0.05);
  const { ref: orb2Ref, offset: orb2Y } = useParallax(-0.04);
  const { ref: orb3Ref, offset: orb3Y } = useParallax(0.03);

  return (
    <div className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-background-50">
      {/* 三层光效 */}
      <div ref={orb1Ref} className="absolute top-20 right-24 w-[380px] h-[380px] rounded-full bg-primary-100/15 blur-[130px] pointer-events-none" style={{ transform: `translateY(${orb1Y}px)` }} />
      <div ref={orb2Ref} className="absolute bottom-20 left-12 w-[320px] h-[320px] rounded-full bg-accent-100/12 blur-[110px] pointer-events-none" style={{ transform: `translateY(${orb2Y}px)` }} />
      <div ref={orb3Ref} className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[260px] h-[260px] rounded-full bg-secondary-100/8 blur-[140px] pointer-events-none" style={{ transform: `translateY(${orb3Y}px)` }} />

      <div className="relative z-10 w-full max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16 py-10 md:py-14">
        {/* 标题区 */}
        <div ref={titleRef} className="text-center mb-12 md:mb-16">
          <span className={`inline-block text-sm font-label font-semibold text-accent-600 uppercase tracking-[0.2em] mb-4 transition-all duration-700 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            评价复杂性
          </span>
          <h2 className={`font-heading text-4xl md:text-5xl lg:text-6xl font-bold text-foreground-950 mb-5 tracking-tight transition-all duration-1000 delay-100 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            为什么<span className="bg-gradient-to-r from-primary-500 to-accent-500 bg-clip-text text-transparent">思政课难评</span>？
          </h2>
          <p className={`text-xl md:text-2xl text-foreground-950 font-body max-w-2xl mx-auto leading-relaxed transition-all duration-1000 delay-200 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
            思政课评价的不是一个点，而是一组<strong className="text-foreground-900">交织在一起</strong>的教学行为
          </p>
        </div>

        {/* 六维度 */}
        <div className={`grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-8 mb-14 md:mb-20 transition-all duration-1000 delay-300 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}>
          {sixDimensions.map((dim, i) => {
            const isAccent = dim.color === 'accent';
            return (
              <div
                key={dim.label}
                className={`group rounded-2xl transition-all duration-700 hover:-translate-y-1 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-12'}`}
                style={{ transitionDelay: `${350 + i * 100}ms` }}
              >
                <div className={`relative h-full p-8 md:p-10 glass-liquid ${isAccent ? 'border-accent-200/30 hover:border-accent-300/50' : 'border-primary-200/30 hover:border-primary-300/50'} rounded-2xl transition-all duration-500`}>
                  <div className="flex items-center gap-4">
                    <span className={`text-3xl md:text-4xl font-heading font-bold ${isAccent ? 'text-accent-500' : 'text-primary-500'}`}>
                      {String(i + 1).padStart(2, '0')}
                    </span>
                    <div className="flex-1 min-w-0">
                      <h4 className="font-heading text-2xl md:text-3xl font-bold text-foreground-950 mb-2">{dim.label}</h4>
                      <p className="text-xl md:text-2xl text-foreground-950 font-body leading-snug">{dim.desc}</p>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        {/* 底部洞察条 */}
        <div className={`flex justify-center transition-all duration-1000 delay-900 ${titleVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
          <div className="inline-flex items-center gap-5 px-8 py-5 glass-liquid border border-primary-200/30 rounded-2xl">
            <span className="text-xl md:text-2xl font-body text-foreground-950">
              AI评课必须先把这些维度<strong className="text-foreground-900">结构化</strong>，才能实现可信评价
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}